import React, { forwardRef, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useSelector } from 'react-redux';
import SectionTitle from '../BookingWidget/components/HowDoYouWantToPay/SectionTitle';
import { colors } from '~/constants/colors';
import { isInsuranceDisabled, isPaymentToggleHidden, isSelfPayOnly } from '~/core/util/location';
import { getPaymentMethodToggleOptions } from '../BookingWidget/util';

const Root = styled.div.withConfig({ componentId: 'sc-120zwb5-0' })`
  display: flex;
  justify-content: center;
  transition: 0.2s ease;
  div:last-of-type > div {
    border-radius: 0 3px 3px 0;
  }
  div:not(:first-of-type):not(:last-of-type) > div {
    border-radius: 0;
    border-left: none;
    border-right: none;
  }
  & > div {
    display: flex;
    flex-grow: 1;
  }
`;

const Option = styled(({ selected, ...rest }) => <div {...rest} />).withConfig({ componentId: 'sc-120zwb5-1' })`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 5px 8px;
  text-align: center;
  cursor: pointer;
  transition: 0.2s ease;
  width: 100%;

  ${(props) =>
    props.selected &&
    css`
      border-radius: 3px 0 0 3px;
      background-color: ${colors.crunchBerry};
      border: 1px solid ${colors.crunchBerry};
      color: ${colors.white};
    `}

  ${(props) =>
    !props.selected &&
    css`
      border: 1px solid ${colors.greyHounds};
      border-radius: 3px 0 0 3px;
      background-color: ${colors.white};
      color: ${colors.greyWorm};
    `}
`;

const PaymentSection = styled.div.withConfig({ componentId: 'sc-120zwb5-2' })`
  margin-top: 30px;
`;

const PaymentMethod = styled.div.withConfig({ componentId: 'sc-120zwb5-3' })`
  border: 1px solid ${colors.greyHounds};
  margin-top: 10px;
  padding: 10px;
  background-color: ${colors.white};
`;

interface HowDoYouWantToPayToggleInterface {
  value: string;
  onChange?: (value: string) => void;
}

const HowDoYouWantToPayToggle = forwardRef(function HowDoYouWantToPayToggle(
  { value, onChange }: HowDoYouWantToPayToggleInterface,
  ref: React.Ref<HTMLSelectElement>
) {
  const [toggleValue, setToggleValue] = useState<string>(value);
  const { t } = useTranslation(['common', 'bookingWidget']);
  const location = useSelector((state: any) => state.location);

  const options = useMemo(() => {
    return getPaymentMethodToggleOptions(location, t);
  }, [location, t]);

  if (isSelfPayOnly(location) || isPaymentToggleHidden(location) || isInsuranceDisabled(location)) {
    return null;
  }

  const handleClick = (val: string) => {
    onChange?.(val);
    setToggleValue(val);
  };

  const isSelected = (val: string) => toggleValue === val;

  return (
    <PaymentSection>
      <SectionTitle location={location} />

      <PaymentMethod>
        <Root>
          {options?.map(({ label, value }, i) => (
            <div key={label}>
              <Option
                id={`${value}-${i}`}
                onClick={() => handleClick(value)}
                selected={isSelected(value)}
              >
                {label}
              </Option>
            </div>
          ))}
        </Root>
      </PaymentMethod>
    </PaymentSection>
  );
});

export default HowDoYouWantToPayToggle;
